import { useEffect, useState } from 'react';
import { Backdrop, Box, Divider, IconButton, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { ObituaryNotice } from '../../data/ObituaryData';
import { MOURNING_MODE, MOURNING_STORAGE_KEY } from '../mourningMode';

/** 按下「訃告」按鈕時派發的事件名稱 */
export const MOURNING_OPEN_EVENT = "pkps:open-obituary";

/** 沒有網站外框的頁面（小程式、電子書），不彈出訃告 */
function isEmbeddedPage() {
    const path = window.location.hash.replace(/^#/, "");
    return path.startsWith("/miniprogram") || path.startsWith("/ebook");
}

/**
 * 訃告（文字版）彈窗
 *
 * - 全站共用，掛在 App.tsx（在 Router 之外，所以用 window.location.hash 判斷路徑）
 * - 每個瀏覽 session 只自動彈出一次（關閉後寫入 sessionStorage）
 * - 可由「訃告」按鈕再次開啟（TopBanner）
 */
export function ObituaryPop() {
    const [open, setOpen] = useState(false);
    const [hidden, setHidden] = useState(() => isEmbeddedPage());

    // 開啟事件：按鈕或自動彈出都會 dispatch 這個 event
    useEffect(() => {
        const onOpen = () => {
            if (isEmbeddedPage()) return;
            setOpen(true);
        };
        window.addEventListener(MOURNING_OPEN_EVENT, onOpen);
        return () => window.removeEventListener(MOURNING_OPEN_EVENT, onOpen);
    }, []);

    // 路由切換時偵測是否進入小程式／電子書頁
    useEffect(() => {
        const onHashChange = () => setHidden(isEmbeddedPage());
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    // 首次進入自動彈出一次
    useEffect(() => {
        if (!MOURNING_MODE || hidden) return;
        if (sessionStorage.getItem(MOURNING_STORAGE_KEY)) return;

        const timer = window.setTimeout(() => {
            sessionStorage.setItem(MOURNING_STORAGE_KEY, "1");
            setOpen(true);
        }, 800);
        return () => window.clearTimeout(timer);
    }, [hidden]);

    const close = () => {
        sessionStorage.setItem(MOURNING_STORAGE_KEY, "1");
        setOpen(false);
    };

    if (!MOURNING_MODE || hidden) return null;

    return (
        <Backdrop
            sx={{
                zIndex: (theme) => theme.zIndex.modal,
                backgroundColor: "rgba(0,0,0,0.82)",
                // 注意：Backdrop 若設 alignItems: flex-start，高度會被內容撐開，
                // 導致視窗內只看到遮罩而看不到內容。所以對齊交給內層容器處理。
            }}
            open={open}
            onClick={close}
        >
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    width: "100%",
                    height: "100%",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    py: { xs: 0, sm: 3 },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        backgroundColor: "#ffffff",
                        // 手機：滿版由頂部開始；桌面：加大寬度
                        borderRadius: { xs: 0, sm: "4px" },
                        width: { xs: "100%", sm: "900px" },
                        maxWidth: { xs: "100%", sm: "94vw" },
                        px: { xs: 2.5, sm: 7 },
                        py: { xs: 4, sm: 6 },
                        // 訃告以黑白呈現
                        boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
                    }}
                >
                <IconButton
                    aria-label="close"
                    onClick={close}
                    sx={{ position: "absolute", right: { xs: 4, sm: 8 }, top: { xs: 4, sm: 8 }, color: "#333" }}
                >
                    <CancelIcon />
                </IconButton>

                {/* 標題 */}
                <Typography
                    align="center"
                    sx={{
                        fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                        fontSize: { xs: "2rem", sm: "3rem" },
                        fontWeight: 700,
                        letterSpacing: "0.6em",
                        color: "#000",
                        mb: 3,
                    }}
                >
                    {ObituaryNotice.title}
                </Typography>

                <Divider sx={{ borderColor: "#000", borderBottomWidth: 2, mb: 3 }} />

                {/* 正文 */}
                {ObituaryNotice.paragraphs.map((para, index) => (
                    <Typography
                        key={index}
                        sx={{
                            fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                            fontSize: { xs: "1rem", sm: "1.2rem" },
                            lineHeight: 2.05,
                            color: "#111",
                            textAlign: "justify",
                            textIndent: para.indented ? 0 : "2em",
                            pl: para.indented ? { xs: 1.5, sm: 3 } : 0,
                            pr: para.indented ? { xs: 1.5, sm: 3 } : 0,
                            mb: 2,
                        }}
                    >
                        {para.text}
                    </Typography>
                ))}

                <Typography
                    sx={{
                        fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                        fontSize: { xs: "1.05rem", sm: "1.35rem" },
                        fontWeight: 700,
                        lineHeight: 2,
                        color: "#000",
                        textAlign: "center",
                        my: 3,
                    }}
                >
                    {ObituaryNotice.closing}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                        lineHeight: 2.05,
                        color: "#111",
                        textAlign: "justify",
                        textIndent: "2em",
                        mb: 2,
                    }}
                >
                    {ObituaryNotice.arrangements}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                        lineHeight: 2.05,
                        color: "#111",
                        textAlign: "justify",
                        textIndent: "2em",
                        mb: 4,
                    }}
                >
                    {ObituaryNotice.farewell}
                </Typography>

                <Divider sx={{ borderColor: "#999", mb: 2.5 }} />

                {/* 署名 */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                            fontSize: { xs: "1.1rem", sm: "1.35rem" },
                            fontWeight: 700,
                            color: "#000",
                        }}
                    >
                        {ObituaryNotice.signOff}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Noto Serif TC', 'Songti TC', 'PMingLiU', serif",
                            fontSize: { xs: "0.9rem", sm: "1.1rem" },
                            color: "#333",
                        }}
                    >
                        {ObituaryNotice.date}
                    </Typography>
                </Box>
                </Box>
            </Box>
        </Backdrop>
    );
}
